import { useCallback } from "react";
import { collapseExpandedComposerCursor, detectComposerTrigger } from "../../composer-logic";
import { type ComposerThreadTarget } from "../../composerDraftStore";
import {
  removeInlineTerminalContextPlaceholder,
  type TerminalContextDraft,
} from "../../lib/terminalContext";

interface UseRemoveComposerTerminalContextInput {
  readonly composerDraftTarget: ComposerThreadTarget;
  readonly composerTerminalContexts: readonly TerminalContextDraft[];
  readonly promptRef: React.MutableRefObject<string>;
  readonly removeComposerDraftTerminalContext: (
    target: ComposerThreadTarget,
    contextId: string,
  ) => void;
  readonly setPrompt: (prompt: string) => void;
  readonly setComposerCursorIfChanged: (cursor: number) => void;
  readonly setComposerTriggerIfChanged: (trigger: ReturnType<typeof detectComposerTrigger>) => void;
}

export function useRemoveComposerTerminalContext(input: UseRemoveComposerTerminalContextInput) {
  const {
    composerDraftTarget,
    composerTerminalContexts,
    promptRef,
    removeComposerDraftTerminalContext,
    setPrompt,
    setComposerCursorIfChanged,
    setComposerTriggerIfChanged,
  } = input;
  return useCallback(
    (contextId: string) => {
      const contextIndex = composerTerminalContexts.findIndex(
        (context) => context.id === contextId,
      );
      if (contextIndex < 0) return;
      const removal = removeInlineTerminalContextPlaceholder(promptRef.current, contextIndex);
      setPrompt(removal.prompt);
      removeComposerDraftTerminalContext(composerDraftTarget, contextId);
      setComposerCursorIfChanged(collapseExpandedComposerCursor(removal.prompt, removal.cursor));
      setComposerTriggerIfChanged(detectComposerTrigger(removal.prompt, removal.cursor));
    },
    [
      composerDraftTarget,
      composerTerminalContexts,
      promptRef,
      removeComposerDraftTerminalContext,
      setComposerCursorIfChanged,
      setPrompt,
      setComposerTriggerIfChanged,
    ],
  );
}
